<template>
  <div class="container-fluid bg-light min-vh-100 py-4">
    <HeaderPage>
      <div class="dashboard mx-auto">

        <!-- Sélecteur d'année -->
        <div class="year-selector text-center mb-4">
          <label for="year" class="me-2">Sélectionnez l'année :</label>
          <select id="year" v-model="selectedYear" @change="loadData" class="form-select d-inline-block w-auto">
            <option v-for="year in availableYears" :key="year" :value="year">
              {{ year }}
            </option>
          </select>
        </div>

        <!-- Graphique CA mensuel -->
        <div class="chart-container mb-4">
          <canvas id="revenueChart"></canvas>
        </div>

        <!-- Statistiques -->
        <div class="statistics d-flex justify-content-around mb-5">
          <div class="stat text-center p-3 bg-white shadow-sm rounded">
            <h3>{{ formatNumber(totalRevenue) }} Ariary</h3>
            <p>CA total</p>
          </div>
          <div class="stat text-center p-3 bg-white shadow-sm rounded">
            <h3>{{ totalOrders }}</h3>
            <p>Commandes</p>
          </div>
          <div class="stat text-center p-3 bg-white shadow-sm rounded">
            <h3>{{ formatNumber(averageOrderValue) }} Ariary</h3>
            <p>Valeur moyenne</p>
          </div>
        </div>

        <!-- Tableau CA mensuel -->
        <div class="table-container bg-white p-3 shadow-sm rounded">
          <h5 class="mb-3">Chiffre d’affaires mensuel — {{ selectedYear }}</h5>
          <table class="table table-striped">
            <thead>
              <tr>
                <th>Mois</th>
                <th class="text-end">CA (Ariary)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(rev, idx) in monthlyRevenue" :key="idx">
                <td>{{ monthLabels[idx] }}</td>
                <td class="text-end">{{ formatNumber(rev) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

      </div>
    </HeaderPage>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import axios from 'axios';
import HeaderPage from './components/HeaderPage.vue';
import { Chart, registerables } from 'chart.js';
Chart.register(...registerables);

// Années disponibles
const availableYears = ref([2021, 2022, 2023, 2024, 2025]);
const currentYear = new Date().getFullYear();
const selectedYear = ref(
  availableYears.value.includes(currentYear)
    ? currentYear
    : availableYears.value[availableYears.value.length - 1]
);

// Données
const monthlyRevenue = ref(Array(12).fill(0));
const totalRevenue = ref(0);
const totalOrders = ref(0);
const averageOrderValue = ref(0);

// Libellés mois
const monthLabels = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

// Format nombre
const formatNumber = num => Number(num).toLocaleString('fr-FR');

// Instance Chart
let chartInstance = null;

// Chargement des données depuis l'API
async function loadData() {
  monthlyRevenue.value = Array(12).fill(0);
  totalOrders.value = 0;
  averageOrderValue.value = 0;

  const API = process.env.VUE_APP_API_URL;

  try {
    const { data } = await axios.get(`${API}/api/tableau/chiffre-affaires`);

    // CA total
    totalRevenue.value = data.chiffre_affaires_total ?? 0;

    // CA mensuel filtré par année
    const filtered = data.chiffre_affaires_mensuel.filter(item =>
      item.annee === Number(selectedYear.value)
    );

    // CA mensuel par mois
    filtered.forEach(item => {
      const index = item.mois - 1;
      monthlyRevenue.value[index] = item.chiffre_affaires_mensuel;
    });

    // Nombre de mois avec CA (comme approximation du nombre de commandes)
    totalOrders.value = filtered.length;

    // Valeur moyenne
    averageOrderValue.value = totalOrders.value
      ? (totalRevenue.value / totalOrders.value).toFixed(2)
      : 0;

    // MAJ Chart.js
    if (chartInstance) {
      chartInstance.data.datasets[0].data = monthlyRevenue.value;
      chartInstance.options.plugins.title.text = `CA en ${selectedYear.value}`;
      chartInstance.update();
    }
  } catch (error) {
    console.error("Erreur API chiffre d'affaires :", error);
  }
}

// Initialisation
onMounted(async () => {
  const ctx = document.getElementById('revenueChart').getContext('2d');
  chartInstance = new Chart(ctx, {
    type: 'line',
    data: {
      labels: monthLabels.map(m => m.substr(0, 3)),
      datasets: [{
        label: `CA en ${selectedYear.value}`,
        data: monthlyRevenue.value,
        borderColor: 'rgba(54,162,235,1)',
        backgroundColor: 'rgba(54,162,235,0.2)',
        fill: true,
        tension: 0.3
      }]
    },
    options: {
      responsive: true,
      plugins: {
        title: {
          display: true,
          text: `CA en ${selectedYear.value}`
        }
      },
      scales: {
        y: {
          ticks: {
            callback: v => formatNumber(v)
          }
        }
      }
    }
  });

  await loadData();
});
</script>

<style scoped>
.dashboard {
  max-width: 1200px;
}
.year-selector select {
  max-width: 120px;
}
.chart-container {
  max-width: 800px;
  margin: 0 auto;
}
.statistics .stat {
  width: 30%;
}
.table-container {
  max-width: 800px;
  margin: 0 auto;
}
</style>
